import { Question, Response, Assessment } from '../types';
import { detectScaleType } from '../utils/scaleLabels';
import { getQuestionOptionsList, getQuestionOptionsAsLegacyShape, getScoringRulesMap } from '../utils/questionUtils';

export interface MaturityScore {
  dimension: string;
  score: number;
  maxScore: number;
  percentage: number;
  questionCount: number;
  answeredCount: number;
}

export interface MaturityAnalysis {
  dimensionScores: MaturityScore[];
  strengths: string[];
  weaknesses: string[];
  recommendations: string[];
}

// Scale mappings organized by scale type to avoid conflicts
const SCALE_MAPPINGS: Record<string, Record<string, number>> = {
  'never-always': {
    'Never': 0,
    'Between Never & Sometimes': 1,
    'Sometimes': 2,
    'Between Sometimes & Always': 3,
    'Always': 4,
  },
  'never-regularly': {
    'Never': 0,
    'Sometimes': 1,
    'Regularly': 2,
    'Always': 3,
  },
  'agree-disagree': {
    'Disagree': 0,
    'Somewhat Disagree': 1,
    'Neutral': 2,
    'Somewhat Agree': 3,
    'Agree': 4,
  },
  'strongly-agree-disagree': {
    'Strongly Disagree': 0,
    'Disagree': 1,
    'Neutral': 2,
    'Agree': 3,
    'Strongly Agree': 4,
  },
  'none-all-partners': {
    'No Agency Partners': 0,
    'Between None & Some': 1,
    'Some': 2,
    'Between Some & All': 3,
    'All': 4,
  },
  'none-all-partners-disclose': {
    'No Agency Partners': 0,
    'Between None & Some': 1,
    'Some': 2,
    'Between Some & All': 3,
    'All': 4,
  },
  'none-all': {
    'No': 0,
    'Between None & Some': 1,
    'Some': 2,
    'Between Some & All': 3,
    'All': 4,
  },
  // Legacy mappings for backward compatibility
  'legacy': {
    'Rarely': 1,
    'Often': 3,
  },
};

class MaturityScoringService {
  // Calculate maturity scores for an assessment
  calculateMaturityScores(
    _assessment: Assessment,
    questions: Question[],
    responses: Response[]
  ): MaturityAnalysis {
    const dimensionScores = this.calculateDimensionScores(questions, responses);

    return {
      dimensionScores,
      strengths: this.identifyStrengths(dimensionScores),
      weaknesses: this.identifyWeaknesses(dimensionScores),
      recommendations: this.generateRecommendations(dimensionScores)
    };
  }

  // Calculate scores for each dimension
  private calculateDimensionScores(questions: Question[], responses: Response[]): MaturityScore[] {
    // Get unique dimensions from questions, sorted by displayOrder
    const dimensionMap = new Map<string, { name: string; displayOrder: number }>();
    questions.forEach(q => {
      if (q.dimension) {
        const dimName = q.dimension.label || q.dimension.name || '';
        if (dimName && !dimensionMap.has(dimName)) {
          dimensionMap.set(dimName, {
            name: dimName,
            displayOrder: q.dimension.displayOrder ?? 999
          });
        }
      }
    });

    // Sort dimensions by displayOrder
    const dimensions = Array.from(dimensionMap.values())
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map(d => d.name);

    return dimensions.map(dimension => {
      const dimensionQuestions = questions.filter(q =>
        (q.dimension?.label === dimension || q.dimension?.name === dimension)
      );
      const dimensionResponses = responses.filter(r =>
        dimensionQuestions.some(q => q.id === r.questionId)
      );

      let totalScore = 0;
      let maxPossibleScore = 0;
      let answeredCount = 0;

      dimensionQuestions.forEach(question => {
        const response = dimensionResponses.find(r => r.questionId === question.id);
        const questionScore = this.calculateQuestionScore(question, response);
        const maxQuestionScore = this.getMaxQuestionScore(question);

        totalScore += questionScore;
        maxPossibleScore += maxQuestionScore;

        if (response && response.answerValue) {
          answeredCount++;
        }
      });

      const percentage = maxPossibleScore > 0 ? (totalScore / maxPossibleScore) * 100 : 0;

      return {
        dimension,
        score: totalScore,
        maxScore: maxPossibleScore,
        percentage: Math.round(percentage * 100) / 100, // Round to 2 decimal places
        questionCount: dimensionQuestions.length,
        answeredCount
      };
    });
  }

  // Calculate score for a single question based on its type and response
  private calculateQuestionScore(question: Question, response?: Response): number {
    if (!response || !response.answerValue) {
      return 0;
    }

    // If it's an AI question, prioritize response.score and do NOT use aiScore (deprecated)
    if (question.isAiQuestion && response.score !== null && response.score !== undefined) {
      return typeof response.score === 'number' ? response.score : parseFloat(response.score.toString());
    }

    const answerValue = response.answerValue;
    const scoringRules = getScoringRulesMap(question);
    const weighting = typeof question.weighting === 'string' ? parseFloat(question.weighting) : (question.weighting || 1);

    switch (question.questionType) {
      case 'binary':
        // Yes/No questions: Yes = 4 points, No = 0 points
        return answerValue === 'Yes' ? 4 * weighting : 0;

      case 'scale':
        // Scale questions: Use scoring rules if available (highest priority)
        if (scoringRules[answerValue] !== undefined) {
          return scoringRules[answerValue] * weighting;
        }

        // Detect the scale type from the question's options
        const scaleType = detectScaleType(getQuestionOptionsAsLegacyShape(question) ?? undefined, scoringRules);

        // Get the appropriate scale mapping based on detected type
        const scaleMapping = SCALE_MAPPINGS[scaleType] || {};
        const legacyMapping = SCALE_MAPPINGS['legacy'] || {};

        // Try exact match in the detected scale mapping
        if (scaleMapping[answerValue] !== undefined) {
          return scaleMapping[answerValue] * weighting;
        }

        // Try legacy mappings as fallback
        if (legacyMapping[answerValue] !== undefined) {
          return legacyMapping[answerValue] * weighting;
        }

        // Try case-insensitive match in the detected scale mapping
        const answerLower = answerValue.toLowerCase();
        const matchedKey = Object.keys(scaleMapping).find(key => key.toLowerCase() === answerLower);
        if (matchedKey) {
          return scaleMapping[matchedKey] * weighting;
        }

        // If still no match, use question options and scoring rules
        const optionsList = getQuestionOptionsList(question);
        if (optionsList.length > 0) {
          const index = optionsList.indexOf(answerValue);
          if (index >= 0 && answerValue in scoringRules) {
            return scoringRules[answerValue] * weighting;
          }
          if (index >= 0) {
            const reversedIdx = optionsList.length - 1 - index;
            return Math.min(reversedIdx, 4) * weighting;
          }
        }

        // Default: 0 if no match
        return 0;

      case 'multiple_choice':
        // Multiple choice: Use scoring rules or default A-E mapping
        if (scoringRules[answerValue] !== undefined) {
          return scoringRules[answerValue] * weighting;
        }
        // Default A-E mapping: A=4, B=3, C=2, D=1, E=0
        const choiceMapping: Record<string, number> = {
          'A': 4, 'B': 3, 'C': 2, 'D': 1, 'E': 0
        };
        return (choiceMapping[answerValue] || 0) * weighting;

      case 'multiselect':
        // Multi-select: Sum of selected options
        if (Array.isArray(answerValue)) {
          return answerValue.reduce((sum, option) => {
            return sum + (scoringRules[option] || 0);
          }, 0) * weighting;
        }
        return 0;

      case 'text':
        // Text responses: Basic scoring based on length and content
        // This is a simplified version - in production, you'd use AI analysis
        const textLength = answerValue.length;
        if (textLength < 10) return 0;
        if (textLength < 50) return 1 * weighting;
        if (textLength < 100) return 2 * weighting;
        if (textLength < 200) return 3 * weighting;
        return 4 * weighting;

      default:
        return 0;
    }
  }

  // Get maximum possible score for a question
  private getMaxQuestionScore(question: Question): number {
    const weighting = typeof question.weighting === 'string' ? parseFloat(question.weighting) : (question.weighting || 1);

    switch (question.questionType) {
      case 'binary':
        return 4 * weighting;

      case 'scale':
        return 4 * weighting;

      case 'multiple_choice':
        return 4 * weighting;

      case 'multiselect':
        // For multi-select, sum all possible option scores
        const multiselectScoringRules = getScoringRulesMap(question);
        const maxMultiSelectScore = Object.values(multiselectScoringRules).reduce((sum, score) => sum + score, 0);
        return maxMultiSelectScore * weighting;

      case 'text':
        return 4 * weighting;

      default:
        return 4 * weighting;
    }
  }

  // Identify strengths (dimensions with highest scores)
  private identifyStrengths(dimensionScores: MaturityScore[]): string[] {
    const sortedDimensions = [...dimensionScores].sort((a, b) => b.percentage - a.percentage);
    return sortedDimensions.slice(0, 3).map(d => d.dimension);
  }

  // Identify weaknesses (dimensions with lowest scores)
  private identifyWeaknesses(dimensionScores: MaturityScore[]): string[] {
    const sortedDimensions = [...dimensionScores].sort((a, b) => a.percentage - b.percentage);
    return sortedDimensions.slice(0, 3).map(d => d.dimension);
  }

  // Generate recommendations based on maturity gaps
  private generateRecommendations(dimensionScores: MaturityScore[]): string[] {
    const recommendations: string[] = [];
    const lowScoringDimensions = dimensionScores.filter(d => d.percentage < 50);

    lowScoringDimensions.forEach(dimension => {
      switch (dimension.dimension) {
        case 'Agility':
          recommendations.push('Implement agile methodologies and improve process documentation');
          break;
        case 'Data':
          recommendations.push('Enhance data collection and analytics capabilities');
          break;
        case 'Governance':
          recommendations.push('Develop comprehensive governance frameworks and policies');
          break;
        case 'Marketer Leadership':
          recommendations.push('Invest in leadership development and team training programs');
          break;
        case 'Partner Mix':
          recommendations.push('Optimize supplier relationships and improve partner management');
          break;
        case 'Social Responsibility':
          recommendations.push('Develop sustainability initiatives and ethical practices');
          break;
        case 'Value':
          recommendations.push('Focus on value delivery and cost optimization strategies');
          break;
      }
    });

    return recommendations.slice(0, 5); // Return top 5 recommendations
  }
}

export const maturityScoringService = new MaturityScoringService();
